import { ColDef, IAggFunc, IAggFuncParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import { customAggFuncs } from '../components/OverviewTable/columns/AggregationConfigs';

type AggFuncSpec = ColDef['aggFunc'];

/** Mirrors ag-grid's own avg/count results: the grid renders `toString()` and sorts on `value`. */
const NUMERIC_AGG_PROTO = Object.freeze({
  toString(this: { value: number | null }) {
    return typeof this.value === 'number' ? this.value.toString() : '';
  },
  toNumber(this: { value: number | null }) {
    return this.value;
  },
});

function numericResult(value: number | null, count?: number) {
  const result = Object.create(NUMERIC_AGG_PROTO) as { value: number | null; count?: number };
  result.value = value;
  if (count !== undefined) {
    result.count = count;
  }
  return result;
}

function sumOf(values: unknown[]): number | null {
  let result: number | null = null;
  for (const value of values) {
    if (typeof value === 'number') {
      result = result === null ? value : result + value;
    }
  }
  return result;
}

function extremeOf(values: unknown[], keep: (candidate: number, current: number) => boolean) {
  let result: number | null = null;
  for (const value of values) {
    if (typeof value === 'number' && (result === null || keep(value, result))) {
      result = value;
    }
  }
  return result;
}

function averageOf(values: unknown[]) {
  let sum = 0;
  let count = 0;
  for (const value of values) {
    if (typeof value === 'number') {
      sum += value;
      count += 1;
    } else if (isNumericAggResult(value) && typeof value.count === 'number') {
      sum += (value.value ?? 0) * value.count;
      count += value.count;
    }
  }
  return numericResult(count > 0 ? sum / count : null, count);
}

function countOf(values: unknown[]) {
  let count = 0;
  for (const value of values) {
    count += isNumericAggResult(value) ? (value.value ?? 0) : 1;
  }
  return numericResult(count);
}

function isNumericAggResult(value: unknown): value is { value: number | null; count?: number } {
  return typeof value === 'object' && value !== null && 'value' in value;
}

/** Ports of ag-grid 35.3.1's aggSum / aggAvg / aggCount / aggMin / aggMax / aggFirst / aggLast — recheck on a major upgrade. */
export const builtInTreeAggFuncs: Record<string, IAggFunc> = {
  sum: (params) => sumOf(params.values),
  min: (params) => extremeOf(params.values, (candidate, current) => candidate < current),
  max: (params) => extremeOf(params.values, (candidate, current) => candidate > current),
  avg: (params) => averageOf(params.values),
  count: (params) => countOf(params.values),
  first: (params) => (params.values.length > 0 ? params.values[0] : null),
  last: (params) => (params.values.length > 0 ? params.values[params.values.length - 1] : null),
};

const warnedAggFuncNames = new Set<string>();
const warnedFieldlessColIds = new Set<string>();

function warnFieldlessAggFunc(colId: string) {
  if (!import.meta.env.DEV || warnedFieldlessColIds.has(colId)) {
    return;
  }
  warnedFieldlessColIds.add(colId);
  console.warn(
    `treeAggFuncs: aggregated column "${colId}" has no field and no own value on the node row; ` +
      'its tree node rows will show a naive re-aggregation of their leaves'
  );
}

function warnUnknownAggFunc(name: string) {
  if (!import.meta.env.DEV || warnedAggFuncNames.has(name)) {
    return;
  }
  warnedAggFuncNames.add(name);
  console.warn(`treeAggFuncs: unknown aggFunc "${name}"; tree node rows will be blank for its columns`);
}

function runAggFunc(original: AggFuncSpec, params: IAggFuncParams): unknown {
  if (typeof original === 'function') {
    return original(params);
  }
  if (typeof original !== 'string') {
    return undefined;
  }
  const resolved = customAggFuncs[original] ?? builtInTreeAggFuncs[original];
  if (!resolved) {
    warnUnknownAggFunc(original);
    return undefined;
  }
  return resolved(params);
}

/** ag-grid's `aggData` wins over row data, so a tree node's own pooled value — `null` included,
 * which is how a pool says "not computable" — has to be returned from the aggFunc itself. */
export function wrapAggFuncForTree(original: AggFuncSpec, field: string | undefined): IAggFunc {
  return (params) => {
    const own = field !== undefined ? get(params.rowNode?.data, field) : ownValueByColId(params);
    if (own !== undefined) {
      return own;
    }
    return runAggFunc(original, params);
  };
}

/** A column that gets its value from a `valueGetter` has no `field` to read the pool by. */
function ownValueByColId(params: IAggFuncParams): unknown {
  const colId = params.column?.getColId();
  const own = colId == null ? undefined : get(params.rowNode?.data, colId);
  if (own === undefined && colId != null) {
    warnFieldlessAggFunc(colId);
  }
  return own;
}
